import { useState } from 'react';
import { useParams } from 'react-router';
import styled from 'styled-components';

import { ActionMenu } from 'ui/actionMenu/ActionMenu';
import { AllowedActionMenuElements, MENU_ALIGNMENTS } from 'ui/actionMenu/types';
import { BUTTON_VARIANTS, IconButton } from 'ui/button';
import { Edit24 } from 'ui/icons/action/Edit24';
import { Overflow } from 'ui/icons/navigation/Overflow';
import { SecondaryPageHeader } from 'ui/pageHeader';
import { spacing } from 'ui/theme';
import { useWindowSize } from 'utils/hooks/useWindowSize';

import { AUA_BASE_PAGE_PATH, CONTENT } from '../constants';
import { useQueryActiveCriteriaCount } from '../hooks/useQueryActiveCriteriaCount';
import { useQueryAnonCriteria } from '../hooks/useQueryAnonCriteria';
import { createActivateOption, createArchiveOption } from '../utils';
import { StatusCell } from './AnonymousCriteriaTable';
import {
  CriteriaBuilderContainer,
  CriteriaDetailsContainer,
  CriteriaDetailsPageContainer,
  Detail,
  PageHeaderSubsection,
  PrimaryDetail,
} from './AnonymousUserTracking.styled';
import CriteriaQueryBuilder from './CriteriaQueryBuilder';
import { CriteriaArchivalModal } from './Modals/CriteriaArchivalModal';
import { EditDescriptionModal } from './Modals/EditDescriptionModal';

import { AnonTrackingAnnouncements } from './Announcements/AnonUserTrackingAnnouncements';

import { AnonCriteriaName } from './AnonCriteriaName';
import { CriteriaDetailsSidebar } from './CriteriaDetailsSidebar';
import { ToggleActiveModal } from './Modals/ToggleActiveModal';

const StyledActionMenu = styled(ActionMenu)`
  margin-left: ${spacing.double};
`;

export const AnonCriteriaDetailsPage = () => {
  const { isMobile } = useWindowSize(768);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isArchivalModalOpen, setIsArchivalModalOpen] = useState(false);
  const [isToggleActiveModalOpen, setIsToggleActiveModalOpen] = useState(false);
  const [isEditDescriptionModalOpen, setIsEditDescriptionModalOpen] = useState(false);

  const { id } = useParams<{ id: string }>();
  const criteriaId = parseInt(id, 10);

  const { criteria, refetch: refetchCriteria } = useQueryAnonCriteria(criteriaId);
  const { limitReached } = useQueryActiveCriteriaCount();

  const actions = criteria ? (
    <StyledActionMenu
      align={MENU_ALIGNMENTS.RIGHT}
      options={[
        ...(!criteria.isArchived
          ? [
              {
                id: 'edit-criteria-description',
                content: (
                  <>
                    <Edit24 />
                    Edit description
                  </>
                ),
                as: AllowedActionMenuElements.Button,
                onClick: () => {
                  setIsEditDescriptionModalOpen(true);
                },
              },
            ]
          : []),
        ...(!criteria.isArchived
          ? [
              createActivateOption({
                criteria,
                setIsToggleActiveModalOpen,
                maxActiveCriteriaLimitReached: limitReached,
              }),
            ]
          : []),
        createArchiveOption({ criteria, setIsArchivalModalOpen }),
      ]}
    >
      <IconButton variant={BUTTON_VARIANTS.SECONDARY}>
        <Overflow />
      </IconButton>
    </StyledActionMenu>
  ) : null;

  return (
    <>
      <AnonTrackingAnnouncements />
      <CriteriaDetailsPageContainer>
        <SecondaryPageHeader
          breadcrumb={{
            content: CONTENT.criteriaDetailsPage.breadcrumbLabel,
            href: AUA_BASE_PAGE_PATH,
          }}
          widthStyle={spacing.PageWidthStyle.normal}
          pageActions={actions}
          pageSubSection={
            criteria && (
              <PageHeaderSubsection>
                <Detail>
                  <StatusCell criteria={criteria} />
                </Detail>
                <PrimaryDetail>ID: {criteria.id}</PrimaryDetail>
              </PageHeaderSubsection>
            )
          }
        >
          <AnonCriteriaName isEditingName={isEditingName} setIsEditingName={setIsEditingName} />
        </SecondaryPageHeader>
        <CriteriaDetailsContainer data-mobile-view={isMobile}>
          <CriteriaBuilderContainer>
            <CriteriaQueryBuilder isReadOnly searchQuery={criteria?.fields?.searchQuery} />
          </CriteriaBuilderContainer>
          {criteria && <CriteriaDetailsSidebar criteria={criteria} isMobileView={isMobile} />}
        </CriteriaDetailsContainer>
        {isArchivalModalOpen && criteria && (
          <CriteriaArchivalModal
            archiving={!criteria.isArchived}
            onClose={() => setIsArchivalModalOpen(false)}
            criteria={criteria}
            onSuccess={refetchCriteria}
          />
        )}
        {isToggleActiveModalOpen && criteria && (
          <ToggleActiveModal
            activating={!criteria.fields.isLive}
            onClose={() => setIsToggleActiveModalOpen(false)}
            criteria={criteria}
            onSuccess={refetchCriteria}
          />
        )}
        {isEditDescriptionModalOpen && criteria && (
          <EditDescriptionModal
            onClose={() => setIsEditDescriptionModalOpen(false)}
            criteria={criteria}
          />
        )}
      </CriteriaDetailsPageContainer>
    </>
  );
};
